import React, { useEffect, useState, useContext } from 'react';
import {
  Container, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TextField, Button, IconButton, Box, Alert, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Person as PersonIcon, Undo as UndoIcon, Lock as LockIcon } from '@mui/icons-material';
import { UserRoleContext } from './Layout';
import { usersApi } from '../services/api';

interface User {
  _id: string;
  username: string;
  role: 'master_admin' | 'admin' | 'tehnolog' | 'direktor';
}

const UserAdmin: React.FC = () => {
  const { role } = useContext(UserRoleContext);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form, setForm] = useState<{ username: string; password: string; role: 'master_admin' | 'admin' | 'tehnolog' | 'direktor' }>({ username: '', password: '', role: 'tehnolog' });
  const [editId, setEditId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [impersonated, setImpersonated] = useState<{ token: string; user: User } | null>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const adminUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const users = await usersApi.getAll();
      setUsers(users);
    } catch (e) {
      setError('Greška pri dohvatanju korisnika');
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name as string]: value }));
  };

  const handleRoleChange = (e: any) => {
    setForm(f => ({ ...f, role: e.target.value }));
  };

  const handleDialogOpen = (user?: User) => {
    if (user) {
      setEditId(user._id);
      setForm({ username: user.username, password: '', role: user.role });
    } else {
      setEditId(null);
      setForm({ username: '', password: '', role: 'tehnolog' });
    }
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditId(null);
    setForm({ username: '', password: '', role: 'tehnolog' });
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      if (!form.username || (!editId && !form.password)) {
        setError('Sva polja su obavezna');
        setLoading(false);
        return;
      }
      if (editId) {
        await usersApi.update(editId, { username: form.username, role: form.role });
        setSuccess('Korisnik izmenjen');
      } else {
        await usersApi.create({ username: form.username, password: form.password, role: form.role });
        setSuccess('Korisnik dodat');
      }
      fetchUsers();
      handleDialogClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Greška pri čuvanju');
    } finally {
      setLoading(false);
    }
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [userMeasurements, setUserMeasurements] = useState<any>(null);
  const [potentialInheritors, setPotentialInheritors] = useState<User[]>([]);
  const [deleteOptions, setDeleteOptions] = useState<{
    inheritTo: string;
    keepOriginal: boolean;
  }>({ inheritTo: '', keepOriginal: false });

  const handleDelete = async (user: User) => {
    setUserToDelete(user);
    setDeleteOptions({ inheritTo: '', keepOriginal: false });
    
    try {
      // Prvo proveri da li korisnik ima merenja
      const response = await fetch(`/api/users/${user._id}/measurements`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserMeasurements(data.measurements);
        setPotentialInheritors(data.potentialInheritors);
        
        if (data.measurements.count > 0) {
          setDeleteDialogOpen(true);
        } else {
          // Ako nema merenja, direktno obriši
          if (window.confirm(`Obrisati korisnika ${user.username}?`)) {
            await performDelete(user._id);
          }
        }
      } else {
        setError('Greška pri proveri merenja korisnika');
      }
    } catch (e: any) {
      setError('Greška pri proveri merenja korisnika');
    }
  };

  const performDelete = async (userId: string) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const deleteData: any = {};
      
      if (deleteOptions.inheritTo) {
        deleteData.inheritTo = deleteOptions.inheritTo;
      } else if (deleteOptions.keepOriginal) {
        deleteData.keepOriginal = true;
      }
      
      await usersApi.delete(userId, deleteData);
      setSuccess('Korisnik obrisan');
      fetchUsers();
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setUserMeasurements(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Greška pri brisanju');
    } finally {
      setLoading(false);
    }
  };

  // Impersonate (prijavi se kao tehnolog)
  const handleImpersonate = async (user: User) => {
    try {
      const res = await usersApi.impersonate(user._id);
      setImpersonated({ token: localStorage.getItem('token') || '', user: adminUser });
      localStorage.setItem('token', res.token);
      localStorage.setItem('user', JSON.stringify(res.user));
      window.location.reload();
    } catch (e) {
      setError('Neuspešna impersonacija');
    }
  };

  // Povratak na admina
  const handleReturnToAdmin = () => {
    if (impersonated) {
      localStorage.setItem('token', impersonated.token);
      localStorage.setItem('user', JSON.stringify(impersonated.user));
      setImpersonated(null);
      window.location.reload();
    }
  };

  // Promena šifre
  const handleChangePassword = (user: User) => {
    setSelectedUser(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const handleSavePassword = async () => {
    if (!selectedUser || !newPassword.trim()) {
      setError('Nova šifra je obavezna');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await usersApi.changeUserPassword(selectedUser._id, newPassword);
      setSuccess('Šifra uspešno promenjena');
      setPasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Greška pri promeni šifre');
    } finally {
      setLoading(false);
    }
  };

  // Prikaz samo za admina ili master_admina
  if (role !== 'admin' && role !== 'master_admin') {
    return <Container maxWidth="md"><Alert severity="error">Samo admin ili master admin može da pristupi ovoj stranici.</Alert></Container>;
  }

  // Provera da li je admin trenutno "ulogovan kao"
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isImpersonating = currentUser.role !== adminUser.role;

  // Filtriraj korisnike za prikaz u tabeli
  const visibleUsers = role === 'master_admin' ? users : (role === 'admin' ? users.filter(u => u.role === 'tehnolog') : users);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>Korisnici</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Prikazano {visibleUsers.length} od {users.length} korisnika
        {role === 'admin' && ' (samo kontrolori)'}
        {role === 'master_admin' && ' (svi korisnici)'}
      </Typography>
      {isImpersonating && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Prijavljeni ste kao: <b>{currentUser.username}</b> ({currentUser.role})
          <Button startIcon={<UndoIcon />} onClick={handleReturnToAdmin} sx={{ ml: 2 }}>
            Vrati se na admina
          </Button>
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleDialogOpen()}>
            Dodaj korisnika
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Korisničko ime</TableCell>
                <TableCell>Rola</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleUsers.map(u => (
                <TableRow key={u._id}>
                  <TableCell>{u.username}</TableCell>
                  <TableCell>
                    {u.role === 'master_admin'
                      ? 'Master admin'
                      : u.role === 'admin'
                      ? 'Administrator'
                      : u.role === 'direktor'
                      ? 'Direktor'
                      : 'Tehnolog'}
                  </TableCell>
                  <TableCell>
                    {/* Edit dugme - master_admin može da edituje sve osim sebe, admin samo tehnolog */}
                    {(role === 'master_admin' || (role === 'admin' && u.role === 'tehnolog')) && u._id !== currentUser.id && (
                      <IconButton onClick={() => handleDialogOpen(u)}><EditIcon /></IconButton>
                    )}
                    
                    {/* Delete dugme - master_admin može da briše sve osim sebe, admin samo tehnolog */}
                    {(role === 'master_admin' || (role === 'admin' && u.role === 'tehnolog')) && u._id !== currentUser.id && (
                      <IconButton onClick={() => handleDelete(u)}><DeleteIcon /></IconButton>
                    )}
                    
                    {/* Change password dugme - samo master_admin može da menja šifre svim korisnicima */}
                    {role === 'master_admin' && u._id !== currentUser.id && (
                      <IconButton onClick={() => handleChangePassword(u)} color="primary">
                        <LockIcon />
                      </IconButton>
                    )}
                    
                    {/* Impersonate dugme - master_admin može da se prijavi kao bilo koji korisnik */}
                    {role === 'master_admin' && u._id !== currentUser.id && !isImpersonating && (
                      <Button size="small" startIcon={<PersonIcon />} onClick={() => handleImpersonate(u)}>
                        Prijavi se kao
                      </Button>
                    )}
                    
                    {/* Impersonate dugme - admin može da se prijavi samo kao tehnolog */}
                    {role === 'admin' && u.role === 'tehnolog' && !isImpersonating && (
                      <Button size="small" startIcon={<PersonIcon />} onClick={() => handleImpersonate(u)}>
                        Prijavi se kao
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>{editId ? 'Izmeni korisnika' : 'Dodaj korisnika'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Ime i prezime"
            name="username"
            value={form.username}
            onChange={handleInput}
            fullWidth
            sx={{ mb: 2 }}
          />
          {!editId && (
            <TextField
              label="Lozinka"
              name="password"
              type="password"
              value={form.password}
              onChange={handleInput}
              fullWidth
              sx={{ mb: 2 }}
            />
          )}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="role-label">Rola</InputLabel>
            <Select
              labelId="role-label"
              name="role"
              value={form.role}
              label="Rola"
              onChange={handleRoleChange}
            >
              <MenuItem value="tehnolog">Tehnolog</MenuItem>
              {role === 'master_admin' && [
                <MenuItem key="admin" value="admin">Administrator</MenuItem>,
                <MenuItem key="master_admin" value="master_admin">Master admin</MenuItem>,
                <MenuItem key="direktor" value="direktor">Direktor</MenuItem>
              ]}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Otkaži</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>{editId ? 'Sačuvaj izmene' : 'Dodaj'}</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog za promenu šifre */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}>
        <DialogTitle>Promeni šifru korisnika</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Korisnik: <strong>{selectedUser.username}</strong> (
                {selectedUser.role === 'master_admin'
                  ? 'Master admin'
                  : selectedUser.role === 'admin'
                  ? 'Administrator'
                  : selectedUser.role === 'direktor'
                  ? 'Direktor'
                  : 'Tehnolog'}
              )
            </Typography>
          )}
          <TextField
            label="Nova šifra"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSavePassword();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Otkaži</Button>
          <Button onClick={handleSavePassword} variant="contained" disabled={loading || !newPassword.trim()}>
            Promeni šifru
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog za opcije brisanja korisnika */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Brisanje korisnika: {userToDelete?.username}
        </DialogTitle>
        <DialogContent>
          {userMeasurements && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" color="warning.main" gutterBottom>
                ⚠️ Korisnik ima {userMeasurements.count} merenja
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Molimo odaberite kako želite da se postupi sa merenjima:
              </Typography>
              
              {userMeasurements.samples && userMeasurements.samples.length > 0 && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Primeri merenja:</Typography>
                  {userMeasurements.samples.map((m: any) => (
                    <Typography key={m.id} variant="body2" sx={{ fontSize: '0.875rem' }}>
                      • {m.productType} - {m.datumProizvodnje}
                    </Typography>
                  ))}
                  {userMeasurements.count > 5 && (
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                      ... i još {userMeasurements.count - 5} merenja
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>Opcije:</Typography>
            
            {/* Opcija 1: Nasledi drugom korisniku */}
            <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'primary.main', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                📋 Nasledi merenja drugom korisniku:
              </Typography>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel>Odaberi korisnika</InputLabel>
                <Select
                  value={deleteOptions.inheritTo}
                  onChange={(e) => setDeleteOptions({
                    ...deleteOptions,
                    inheritTo: e.target.value,
                    keepOriginal: false
                  })}
                  label="Odaberi korisnika"
                >
                  <MenuItem value="">-- Odaberi korisnika --</MenuItem>
                  {potentialInheritors.map((inheritor) => (
                    <MenuItem key={inheritor._id} value={inheritor._id}>
                      {inheritor.username} ({inheritor.role === 'master_admin' ? 'Master admin' : inheritor.role === 'admin' ? 'Administrator' : 'Kontrolor'})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Opcija 2: Ostavi pod originalnim imenom */}
            <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'secondary.main', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                📝 Ostavi merenja pod originalnim imenom:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Merenja će ostati pod imenom "{userToDelete?.username}" i dalje će biti vidljiva u sistemu.
              </Typography>
              <Button
                variant={deleteOptions.keepOriginal ? "contained" : "outlined"}
                color="secondary"
                onClick={() => setDeleteOptions({
                  ...deleteOptions,
                  keepOriginal: true,
                  inheritTo: ''
                })}
                fullWidth
              >
                {deleteOptions.keepOriginal ? "✓ Odabrano" : "Odaberi ovu opciju"}
              </Button>
            </Box>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Otkaži
          </Button>
          <Button 
            onClick={() => performDelete(userToDelete!._id)}
            variant="contained" 
            color="error"
            disabled={loading || (!deleteOptions.inheritTo && !deleteOptions.keepOriginal)}
          >
            {loading ? 'Brisanje...' : 'Obriši korisnika'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserAdmin; 